<template>
  <div class="spl-export-proof-detail-table" data-cy="export-proof-detail-table">
    <table v-if="loadedExportProof">
      <thead>
        <tr class="spl-parcel-table__head">
          <th
            v-for="(header, index) in loadedExportProof.Export_Proof_Layout"
            :key="'dashboardPrecariageHeader' + index"
            @click="toggleSort(header.Name)">
            <span
              v-if="findSort(sortedFields, header.Name)"
              :class="findSort(sortedFields, header.Name).direction"
              class="order-arrow" />
            {{ header.Label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(exportProof, exportProofIndex) in exportProofResult" :key="'exportProofTable' + exportProofIndex">
          <td
            v-for="(field, fieldIndex) in loadedExportProof.Export_Proof_Layout"
            :key="'dashboardPrecariageField' + exportProofIndex + '.' + fieldIndex"
            :class="{'spl-parcel-table__field--number': isNumber(exportProof[field.Name]),
                     'spl-parcel-table__field--long': field.long,
                     'spl-parcel-table__field--bold': field.bold}">
            <span>
              {{ formatField(exportProof[field.Name], field.Name, field.Format) }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { isNumber, find, findIndex, orderBy, round } from 'lodash'
import constants from '@/constants'
import Helpers from '@/helpers.vue'
export default {
  name: 'ExportProofListTable',
  mixins: [Helpers],
  props: [],
  data () {
    return {
      sortedFields: []
    }
  },
  computed: {
    ...mapState('exportProof', ['exportProofResult', 'loadedExportProof'])
  },
  watch: {
    rows (newValue) {
      this.listRows = newValue
    },
    // The sort column has changed
    sort_column_index (newValue) {
      this.sortColumnIndex = newValue
    },
    // The sort order has changed
    sort_order (newValue) {
      this.sortOrder = newValue
    }
  },
  methods: {
    ...mapMutations('exportProof', ['setExportProofResult']),
    /// ******************************** SORT FUNCTION *******************************
    sortParcelListBy (column) {
      // Call the sort changed in the export proof
      this.$emit('sortChanged', column)
    },
    toggleSort (field) {
      // Finds the sorted field in the array
      var sortedField = find(this.sortedFields, { field: field })
      if (sortedField) {
        // Switches to desc when asc already selected
        if (sortedField.direction === 'asc') {
          sortedField.direction = 'desc'
        } else {
          // removes the sorting when desc was selected
          this.sortedFields.splice(findIndex(this.sortedFields, { field: field }))
        }
      } else {
        // adds the field when not yet present
        this.sortedFields.push({ direction: 'asc', field: field })
      }
      this.sortData()
    },
    // Sorting the data
    sortData () {
      // Sort fields and directions seperate for lodash orderBy method
      let sortFields = []
      let sortDirections = []
      // Create the sort fields
      for (let i = 0; i < this.sortedFields.length; i++) {
        const sortElement = this.sortedFields[i]
        sortFields.push(sortElement.field)
        sortDirections.push(sortElement.direction)
      }
      // Default sorting depending on grouping
      if (sortFields.length === 0) {
        sortFields = [constants.REFERENCE_TYPE_NAME.COUNTER_NUMBER]
        sortDirections = ['asc']
      }
      // Use the lodash orderBy method for sorting
      this.setExportProofResult(orderBy(this.exportProofResult, sortFields, sortDirections))
    },
    // Special formatting for fields
    formatField (field, name, type) {
      if (type === 'Number.2') {
        return round(field, 2).toFixed(2)
      } else if (type === 'Number.3') {
        return round(field, 3).toFixed(3)
      } else if (type === 'Date') {
        return this.formatDate(field, false, 'YYYY-MM-DD')
      } else if (type === 'Timestamp') {
        return this.formatDate(field, true, 'YYYY-MM-DD HH:mm')
      }
      // empty fields will be removed
      if (field == null) {
        return ''
      }
      return field
    },
    // find the right sort value for displaying arrows
    findSort (array, value) {
      return find(array, { field: value })
    },
    // including of helper since lodash not available in vue template
    isNumber (value) {
      return isNumber(value)
    }
  }
}
</script>

<style lang="scss">
  @import '../../../variables.scss';
  .spl-export-proof-detail-table {
    margin: 0 70px;
    width: calc(100% - 138px);
    height: calc(100vh - 215px);
    overflow: auto;

    table {
      border-spacing: 0;
      table-layout: fixed;
    }
    &__head {
      th {
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 3px 5px 0 rgba(0, 0, 0, 0.1);
      }
      &--sticky {
        position: sticky;
        left: 41px;
        z-index: 3 !important;
      }
      &--checkbox {
        .spl-checkbox {
          margin: 10px 2px 0 6px;
        }
        position: sticky;
        left: 0px;
        z-index: 3 !important;
      }
    }
    &__row {
      &--selected {
        background-color: $tableRowBackgroundColorSelected !important;
      }
      &--deleted {
        opacity: 0.3;
        background-color: lightgrey !important;
      }
    }
    &__field {
      &--number {
        text-align: right;
        width: 70px;
      }
      &--sticky {
        position: sticky;
        left: 41px;
        box-shadow: 3px 0px 3px 0px rgba(0, 0, 0, 0.2);
        background-color: $tableHeaderBackgroundColor;
      }
      &--checkbox {
        .spl-checkbox {
          margin: 0 2px 0 6px;
        }
        position: sticky;
        background-color: $tableHeaderBackgroundColor;
        left: 0;
      }
      &--long {
        max-width: 400px !important;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      &--success {
        border-bottom: 2px solid $green !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $green;
      }
      &--warning {
        border-bottom: 2px solid $darkYellow !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $darkYellow;
      }
      &--error {
        border-bottom: 2px solid $red !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $red;
      }
      &--bold {
        font-weight: 600;
      }
      &--empty:hover {
        background-color: $tableRowBackgroundColorHover !important;
      }
    }
    thead {
      th {
        position: sticky;
        top: 0;
        text-align: left;
        font-family: $mono;
        font-size: 9px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        line-height: 12px;
        background-color: $tableHeaderBackgroundColor;
        padding: 0px 5px  0px 5px;
        height: 40px;
        border-bottom: 1px solid $tableHeaderBorderColor;
      }

      th:hover {
        background-color: $tableColumnBackgroundColorHover;
        cursor: pointer;
      }
    }
    tbody {

      tr {
        background-color: $tableRowBackgroundColor;
      }

      tr:hover {
        background-color: $tableRowBackgroundColorHover;
      }

      td {
        border-style: solid;
        border-width: 0 1px 1px 0;
        border-color: $tableRowBorderLowColor;
        border-bottom-color: $tableRowBorderColor;
        font-size: 12px;
        padding: 5px;
        padding-top: 8px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 150px;
      }

      td:hover {
        background-color: $tableColumnBackgroundColorHover;
      }

      td:first-child {
        border-left-width: 1px;
      }
    }
}
</style>
